import React, { useCallback, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  fetchReadiness,
  type ReadinessResponse,
} from '../../services/systemHealthService';
import { useWebSocketConnection } from '../../contexts/WebSocketContext';

const CHECK_LABELS: Record<string, string> = {
  database: '데이터베이스',
  gateway_hls: '게이트웨이 HLS 저장소',
  yolo: 'YOLO 모델',
  openpose_enabled: 'OpenPose',
  gpu_allocation: 'GPU 할당',
  gpu_device_count: 'GPU 장치 수',
};

function checkOk(value: string): boolean {
  const v = (value || '').toLowerCase();
  return v === 'ok' || v === 'true' || v === 'unloaded';
}

function checkDisplay(
  key: string,
  value: string
): { tone: 'ok' | 'warn' | 'info'; secondary: string } {
  const v = String(value || '').toLowerCase();
  if (key === 'openpose_enabled') {
    if (v === 'true' || v === '1') {
      return { tone: 'ok', secondary: '사용 중' };
    }
    return { tone: 'info', secondary: '사용 안 함 (기본·정상)' };
  }
  if (key === 'gpu_device_count') {
    const n = parseInt(String(value), 10);
    if (Number.isFinite(n) && n > 0) {
      return { tone: 'ok', secondary: `${n}대 감지 (CUDA 인덱스 0~${n - 1})` };
    }
    return { tone: 'warn', secondary: 'GPU 없음 — CPU 추론 또는 드라이버 확인' };
  }
  if (checkOk(v)) {
    return { tone: 'ok', secondary: String(value) };
  }
  if (v.startsWith('fail')) {
    return { tone: 'warn', secondary: String(value) };
  }
  if (v.startsWith('skip')) {
    return { tone: 'info', secondary: String(value) };
  }
  return { tone: 'warn', secondary: String(value) };
}

const WS_LABELS: Record<string, string> = {
  connected: '연결됨',
  connecting: '연결 중',
  disconnected: '끊김',
};

const SystemReadinessWidget: React.FC = () => {
  const { connectionStatus, reconnect } = useWebSocketConnection();
  const [data, setData] = useState<ReadinessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchReadiness();
      setData(res);
      setError(null);
    } catch {
      setError('준비 상태 API(/api/health/ready)에 연결실패.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  const serverDegraded = data?.status === 'degraded';
  const wsDown = connectionStatus !== 'connected';
  const overallDegraded = serverDegraded || wsDown;

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">시스템 준비 상태 (Readiness)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {data && (
            <Chip
              size="small"
              label={overallDegraded ? '주의' : 'ready'}
              color={overallDegraded ? 'warning' : 'success'}
            />
          )}
          <Tooltip title="새로고침">
            <span>
              <IconButton size="small" onClick={load} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && <Alert severity="warning">{error}</Alert>}

      {wsDown && (
        <Alert severity="info" sx={{ mb: 1 }}>
          서버 API(데이터베이스 등)는 정상일 수 있으나, 실시간 알림·이벤트는 관제 WebSocket 연결이
          필요
          <Button size="small" sx={{ ml: 1 }} onClick={() => reconnect()}>
            연결 시도
          </Button>
        </Alert>
      )}

      <List dense disablePadding sx={{ mb: data?.checks ? 1 : 0 }}>
        <ListItem disableGutters>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {connectionStatus === 'connected' ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <WarningIcon color="warning" fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary="관제 WebSocket"
            secondary={WS_LABELS[connectionStatus] || connectionStatus}
            primaryTypographyProps={{ variant: 'body2' }}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
      </List>

      {data?.checks && (
        <List dense disablePadding>
          {Object.entries(data.checks).map(([key, value]) => {
            const disp = checkDisplay(key, String(value));
            const icon =
              disp.tone === 'ok' ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : disp.tone === 'info' ? (
                <InfoOutlinedIcon color="info" fontSize="small" />
              ) : (
                <WarningIcon color="warning" fontSize="small" />
              );
            return (
              <ListItem key={key} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
                <ListItemText
                  primary={CHECK_LABELS[key] || key}
                  secondary={disp.secondary}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default SystemReadinessWidget;
